import { useState, useRef, useEffect } from "react";
import "./chat-ai-evo.component.scss";
import { AIService, SessionService } from "../../../services";
import { responseErros } from "../../../utils";
import { Box } from "../../../components";

const mensagemBoasVindas = {
  id: 1,
  tipo: "bot",
  texto: (
    <>
      Olá! Eu sou o <strong>Evo</strong>, seu assistente do SIAF Evolution. 👋
      <br />
      <br />
      Posso te ajudar com dúvidas sobre <strong>Pessoas</strong>,{" "}
      <strong>Produtos</strong>, <strong>Orçamentos</strong>,{" "}
      <strong>Pedidos</strong>, <strong>NF Saída</strong>,{" "}
      <strong>Ordem de Serviços</strong>, <strong>Caixa</strong> e muito mais.
      <br />
      <br />
      Como posso te ajudar hoje?
    </>
  ),
};

const ChatAiEvo = () => {
  const [session, setSession] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [mensagens, setMensagens] = useState([mensagemBoasVindas]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const mensagensEndRef = useRef(null);

  const aiService = new AIService();
  const sessionService = new SessionService();

  useEffect(() => {
    if (!aberto) return;
    const storedChatId = sessionService.getChatSessionId();
    if (storedChatId) {
      buscarSessionChat(storedChatId);
      return;
    } else criarSessionChat();
  }, [aberto]);

  useEffect(() => {
    mensagensEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensagens]);

  async function criarSessionChat() {
    const res = await aiService.createChatSession();
    if (res.isAxiosError) return responseErros(res);
    if (res?.data) {
      setSession({ id: res.data.id, title: res.data.title });
      sessionService.setChatSessionId(res.data.id);
    }
  }

  async function buscarSessionChat(id) {
    const res = await aiService.getChatSession(id);
    if (res.isAxiosError) return responseErros(res);
    if (res?.data) {
      setSession({ id, title: res.data.title });
      const mensagens = (res.data?.messages || [])
        .filter(
          ({ role }) =>
            role.toUpperCase() === "USER" || role.toUpperCase() === "ASSISTANT",
        )
        .map(({ id, role, content }) => ({
          id,
          tipo: role.toUpperCase() === "USER" ? "usuario" : "bot",
          texto: content,
        }));
      setMensagens(mensagens);
    }
  }

  useEffect(() => {
    function handleEsc(e) {
      if (e.key === "Escape") setAberto(false);
    }
    if (aberto) {
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }
  }, [aberto]);

  function enviarMensagem() {
    const texto = input.trim();
    if (!texto || streaming) return;
    const mensagemUsuario = {
      id: Date.now(),
      tipo: "usuario",
      texto,
    };
    setMensagens((prev) => [...prev, mensagemUsuario]);
    setInput("");
    if (!session?.id) return;
    enviarMensagemStream(session.id, texto);
  }

  async function enviarMensagemStream(sessionId, texto) {
    const botMsgId = Date.now() + 1;
    const updateMensagem = (updater) => {
      setMensagens((prev) =>
        prev.map((m) =>
          m.id === botMsgId ? { ...m, texto: updater(m.texto) } : m,
        ),
      );
    };

    const setMensagemTexto = (texto) => {
      setMensagens((prev) =>
        prev.map((m) => (m.id === botMsgId ? { ...m, texto } : m)),
      );
    };

    setMensagens((prev) => [...prev, { id: botMsgId, tipo: "bot", texto: "" }]);
    setStreaming(true);
    try {
      const streamGen = await aiService.sendMessageStream(sessionId, {
        content: texto,
      });

      for await (const { event, data } of streamGen) {
        if (event === "chunk" && data?.type === "text-delta") {
          updateMensagem((t) => t + (data.text || ""));
          continue;
        }
        if (event === "complete" && data?.text) {
          setMensagemTexto(data.text);
          continue;
        }
        if (event === "error") {
          setMensagemTexto(
            `Erro: ${data?.message || "Falha ao obter resposta."}`,
          );
          break;
        }
        if (event === "done" || data === "[DONE]") {
          break;
        }
      }
    } catch (err) {
      setMensagemTexto("Erro ao conectar ao assistente.");
    } finally {
      setStreaming(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  }

  function renderFormattedText(content) {
    if (!content) return null;
    if (typeof content !== "string") return content;

    const lines = content.split(/\r?\n/);
    let strongKey = 0;

    return lines.map((line, idx) => {
      const parts = [];
      let lastIndex = 0;
      const boldRegex = /\*\*(.+?)\*\*/g;
      let m;
      while ((m = boldRegex.exec(line)) !== null) {
        if (m.index > lastIndex) {
          parts.push(line.substring(lastIndex, m.index));
        }
        parts.push(<strong key={`b-${strongKey++}`}>{m[1]}</strong>);
        lastIndex = m.index + m[0].length;
      }
      if (lastIndex < line.length) parts.push(line.substring(lastIndex));

      return (
        <span key={idx}>
          {parts.map((p) => (typeof p === "string" ? p : p))}
          {idx < lines.length - 1 && <br />}
        </span>
      );
    });
  }

  return (
    <>
      {aberto && (
        <Box
          className="chat-evo-container"
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 9999,
          }}
        >
          <Box className="chat-evo-header">
            <Box className="chat-evo-header-info">
              <Box className="chat-evo-avatar">
                <i className="ph-fill ph-robot" style={{ color: "#fff" }} />
              </Box>
              <Box>
                <Box className="chat-evo-nome">Evo</Box>
                <Box className="chat-evo-subtitulo">
                  Assistente do SIAF Evolution
                </Box>
              </Box>
            </Box>
            <Box className="chat-evo-header-acoes">
              <button
                className="chat-evo-btn-icone"
                onClick={() => setAberto(false)}
                title="Fechar"
              >
                <i className="ph ph-x" style={{ color: "#fff" }} />
              </button>
            </Box>
          </Box>

          <Box className="chat-evo-mensagens">
            {mensagens.map((msg) => (
              <Box
                key={msg.id}
                className={`chat-evo-msg chat-evo-msg--${msg.tipo}`}
              >
                {msg.tipo === "bot" && (
                  <Box className="chat-evo-msg-avatar">
                    <i className="ph-fill ph-robot" style={{ color: "#fff" }} />
                  </Box>
                )}

                <Box className="chat-evo-msg-balao">
                  {msg.tipo === "bot" && streaming && msg.texto === "" ? (
                    <span className="chat-evo-typing">
                      <span />
                      <span />
                      <span />
                    </span>
                  ) : (
                    renderFormattedText(msg.texto)
                  )}
                </Box>
              </Box>
            ))}
            <div ref={mensagensEndRef} />
          </Box>

          <Box className="chat-evo-rodape">
            <Box className="chat-evo-input-area">
              <input
                className="chat-evo-input"
                placeholder={
                  streaming ? "Aguarde..." : "Pergunte algo ao Evo..."
                }
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={streaming}
              />
              <button
                className="chat-evo-btn-enviar"
                onClick={enviarMensagem}
                disabled={!input.trim() || streaming}
                title="Enviar"
              >
                <i
                  className="ph-fill ph-paper-plane-tilt"
                  style={{ color: "#fff" }}
                />
              </button>
            </Box>
            <p className="chat-evo-disclaimer mt-3 mb-2">
              O Evo pode cometer erros. Verifique informações importantes.
            </p>
          </Box>
        </Box>
      )}

      {!aberto && (
        <button
          className="chat-evo-fab"
          onClick={() => setAberto((prev) => !prev)}
          title="Chat Evo"
        >
          <i
            className="ph-fill ph-sparkle chat-evo-fab-main-icon"
            style={{ color: "#fff" }}
          />
          <i
            className="chat-evo-fab-bg-icon chat-evo-fab-bg-icon--1 ph-fill ph-star-four"
            style={{ color: "#fff" }}
          />
          <i
            className="chat-evo-fab-bg-icon chat-evo-fab-bg-icon--2 ph-fill ph-star-four"
            style={{ color: "#fff" }}
          />
          <i
            className="chat-evo-fab-bg-icon chat-evo-fab-bg-icon--3 ph-fillph-star-four"
            style={{ color: "#fff" }}
          />
        </button>
      )}
    </>
  );
};

export default ChatAiEvo;
